import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAllBankersAdmin } from "../../../services/admin.service";
import { Pagination, Chip, IconButton } from "@mui/material";
import {
  Search,
  Person,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import Loader from "../../../common/loader";
import { Banker } from "../../../validation/admin.validation";
import BankerCardView from "./BankerCardView";
import BankerTableView from "./BankerTableView";

const AdminBankers: React.FC = () => {
  const navigate = useNavigate();
  const [bankers, setBankers] = useState<Banker[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchBankers = async (params: any) => {
    setLoading(true);
    try {
      const response = await getAllBankersAdmin(
        params.page,
        params.limit,
        params.search
      );
      if (response) {
        setBankers(response.data);
        setPaginationData({
          page: response.page,
          limit: response.limit,
          total: response.total,
        });
      }
    } catch (error) {
      console.error("Error fetching bankers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = {
      page: paginationData.page,
      limit: paginationData.limit,
      search,
    };
    fetchBankers(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.page]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const params = { page: 1, limit: paginationData.limit, search };
      fetchBankers(params);
      setPaginationData((prev) => ({ ...prev, page: 1 }));
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleViewDetail = (banker: Banker) => {
    navigate(`/admin/banker-details/${banker.id}`);
  };

  const handlePageChange = (_: any, value: number) => {
    setPaginationData((prev) => ({ ...prev, page: value }));
  };

  const handleViewModeChange = (newViewMode: "card" | "table") => {
    setViewMode(newViewMode);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-5 mb-4 lg:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by name, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center gap-4">
              <Chip
                icon={<Person />}
                label={`Total Bankers: ${paginationData.total}`}
                variant="filled"
                color="primary"
                size="medium"
                sx={{
                  fontWeight: "bold",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
                            <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
                <IconButton
                  onClick={() => handleViewModeChange("card")}
                  className={`!p-2 ${viewMode === "card" ? "bg-gray-100" : ""}`}
                >
                  <ViewModuleIcon
                    className={
                      viewMode === "card" ? "text-black" : "text-gray-500"
                    }
                  />
                </IconButton>
                <IconButton
                  onClick={() => handleViewModeChange("table")}
                  className={`!p-2 ${
                    viewMode === "table" ? "bg-gray-100" : ""
                  }`}
                >
                  <ViewListIcon
                    className={
                      viewMode === "table" ? "text-black" : "text-gray-500"
                    }
                  />
                </IconButton>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && viewMode === "card" ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : viewMode === "card" ? (
          <BankerCardView
            bankers={bankers}
            loading={loading}
            handleViewDetail={handleViewDetail}
          />
        ) : (
          <BankerTableView
            bankers={bankers}
            loading={loading}
            handleViewDetail={handleViewDetail}
          />
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm mt-6">
          <Pagination
            count={Math.ceil(paginationData.total / paginationData.limit)}
            page={paginationData.page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="medium"
            className="self-center sm:self-auto"
          />
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-right">
            Showing {(paginationData.page - 1) * paginationData.limit + 1} to{" "}
            {Math.min(
              paginationData.page * paginationData.limit,
              paginationData.total
            )}{" "}
            of {paginationData.total} results
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBankers;
